/**
 * Client-side email validation matching the server-side trigger in
 * supabase/migrations/20260514120000_email_normalization.sql.
 *
 * Both sides MUST agree on:
 *   - The disposable-domain blocklist (we ship a subset here for speed; the
 *     DB has the full list and is authoritative)
 *   - The canonical-form rules (strip +alias, Gmail dots)
 *
 * Purpose:
 *   - Fast UX feedback to the user before we call Supabase Auth
 *   - Avoid wasted magic-link email sends for obviously-bad addresses
 *   - The trigger is still the security boundary — never trust the client.
 */

// Subset of the disposable-domain blocklist for client-side fast-fail.
// Kept short to keep bundle small; the DB has the full list as ground truth.
const DISPOSABLE_DOMAINS = new Set<string>([
  "mailinator.com",
  "mailinator.net",
  "yopmail.com",
  "yopmail.net",
  "guerrillamail.com",
  "guerrillamailblock.com",
  "grr.la",
  "sharklasers.com",
  "10minutemail.com",
  "10minutemail.net",
  "temp-mail.org",
  "temp-mail.io",
  "tempmail.com",
  "tempmail.net",
  "tempmailo.com",
  "maildrop.cc",
  "throwaway.email",
  "getnada.com",
  "nada.email",
  "mintemail.com",
  "dispostable.com",
  "mailcatch.com",
  "trashmail.com",
  "trashmail.net",
  "trashmail.de",
  "spam4.me",
  "spambox.us",
  "emailondeck.com",
  "moakt.com",
  "mohmal.com",
  "inboxbear.com",
  "emltmp.com",
  "mytemp.email",
  "fakeinbox.com",
  "mailnesia.com",
  "mvrht.com",
]);

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Canonical-form email — mirrors public.normalize_email() in SQL. */
export function normalizeEmail(email: string): string {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0) return trimmed;

  let local = trimmed.slice(0, at);
  let domain = trimmed.slice(at + 1);

  // Strip plus-alias
  const plus = local.indexOf("+");
  if (plus >= 0) local = local.slice(0, plus);

  // Gmail dot-folding
  if (domain === "gmail.com" || domain === "googlemail.com") {
    local = local.replace(/\./g, "");
    domain = "gmail.com";
  }

  return `${local}@${domain}`;
}

export type EmailValidationResult =
  | { ok: true; canonical: string }
  | { ok: false; reason: string };

/**
 * Validate an email at signup time. Returns a friendly reason string
 * on failure (safe to show in the UI).
 *
 * The duplicate-alias check requires a DB lookup so it stays server-side.
 * A `stephen+test@gmail.com` signup will pass this check IF no existing
 * `stephen@gmail.com` account exists; the DB trigger rejects it after that.
 */
export function validateEmailForSignup(email: string): EmailValidationResult {
  const trimmed = email.trim();

  if (!trimmed) {
    return { ok: false, reason: "Please enter your email address." };
  }
  if (!EMAIL_FORMAT.test(trimmed)) {
    return { ok: false, reason: "That doesn't look like a valid email address." };
  }

  const domain = trimmed.split("@")[1].toLowerCase();
  if (DISPOSABLE_DOMAINS.has(domain)) {
    return {
      ok: false,
      reason: "Disposable email addresses aren't allowed. Please use a permanent email.",
    };
  }

  return { ok: true, canonical: normalizeEmail(trimmed) };
}
